import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn";

// One source of truth for every CTA in the app. Adding a new variant
// is local; tuning hover/focus/disabled treatment is one diff.
//
// Variants map to existing brand patterns found across routes:
//   primary   — green CTA (Start session, Start game, Join, Play)
//   secondary — neutral fill (Refresh, Back, Choose different lesson)
//   danger    — red destructive (End session, error banner refresh)
//   ghost     — transparent (cancel, dismiss)
//
// Sizes match the three existing button densities in the app.
const buttonVariants = cva(
  // Base: alignment, transition, focus ring, disabled treatment.
  [
    "inline-flex items-center justify-center font-semibold",
    "transition-all duration-150",
    "focus-visible:outline-none",
    "focus-visible:ring-2 focus-visible:ring-offset-2",
    "focus-visible:ring-offset-brand-ink",
    "disabled:opacity-40 disabled:cursor-not-allowed",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-brand-green text-white",
          "hover:bg-brand-greenHi",
          "focus-visible:ring-brand-green",
        ].join(" "),
        secondary: [
          "bg-brand-ink2 text-white",
          "hover:bg-brand-ink3",
          "focus-visible:ring-brand-teal",
        ].join(" "),
        danger: [
          "bg-brand-bad text-white",
          "hover:brightness-110",
          "focus-visible:ring-brand-bad",
        ].join(" "),
        ghost: [
          "bg-transparent text-white",
          "hover:bg-white/10",
          "focus-visible:ring-white/30",
        ].join(" "),
      },
      size: {
        sm: "h-9  px-3  rounded-lg  text-sm",
        md: "h-11 px-5  rounded-xl  text-base",
        lg: "h-14 px-6  rounded-2xl text-lg",
      },
      block: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      block: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, block, className, type = "button", ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size, block }), className)}
      {...rest}
    />
  ),
);
Button.displayName = "Button";
